import { CubePresenter, Logger, NOOP_VOID } from 'wdc-cube';
import { TableModel } from '../../../../components/table';
import { reuseRows } from '../../../../components/rows';
import { formatCnpj, maskPhone } from '../../../../domain';
import { CompanyApi } from './companies.api';
import { CollaboratorsKeys } from '../collaborators/collaborators.key';
import { CompanyConfirmKeys } from './confirm/company-confirm.key';
import { CompanyEditKeys } from './edit/company-edit.key';
import { CompaniesKeys } from './companies.key';
import { ACTION_COLUMN, COLUMNS, DEFAULT_SORT } from './company-table';
import { CompaniesScope, CompanyColumnScope, CompanyRowScope, StatusFilterScope, StatusOptionScope } from './companies.scope';
const LOG = Logger.get('Mormaii.CompaniesPresenter');
// @Inject
const companyApi = CompanyApi.INSTANCE;
const UNFILTERED = {};
/** Quanto se espera depois da ultima tecla antes de buscar - o mesmo dos Monitoramentos. */
const BUSCA_DEBOUNCE_MS = 400;
/** Os sete campos de texto do filtro, na ordem em que a tela os mostra. */
const CAMPOS_DE_TEXTO = [
    'cnpj',
    'razaoSocial',
    'nomeFantasia',
    'ramoAtividade',
    'contactName',
    'contactEmail',
    'contactPhone'
];
/**
 * A lista de empresas.
 *
 * O `GET /v2/companies` nao aceita filtro: traz tudo, e filtrar e ordenar acontecem
 * aqui. O painel antigo faz igual. A diferenca e que os oito filtros agora estao
 * no endereco, e que os dois dialogos - cadastro e confirmacao - sao places.
 */
export class CompaniesPresenter extends CubePresenter {
    constructor(app) {
        super(app, new CompaniesScope());
        /**
         * O modelo da tabela, so para a geometria: aqui quem ordena e o servidor. A largura
         * escolhida antes dele, na chave do antigo `ColumnWidths`, e lida na primeira vez.
         */
        this.table = companyTableModel(() => this.app.currentSession?.id);
        this.parentSlot = NOOP_VOID;
        this.dialogSlot = NOOP_VOID;
        this.at = { criteria: {}, page: 1 };
        /** Tudo o que o servidor devolveu, antes de filtrar e ordenar. */
        this.all = [];
        this.pending = 0;
    }
    async applyParameters(intent, initialization, last) {
        const keys = new CompaniesKeys(this.app, intent);
        // A guarda de papel, que no painel antigo era o `ProtectRouter` repetido em
        // cada rota. Antes de qualquer rede: recusada, a tela nem chega a montar.
        if (!(await keys.owner?.allowEntry('companies', intent))) {
            return false;
        }
        keys.list = this;
        // Atributos da intencao: lidos a cada chamada, como os slots. Vem de quem esta
        // acima na composicao, e reler custa nada.
        this.owner = keys.owner;
        if (initialization) {
            this.scope.onCnpjChanged = this.handleTextChanged.bind(this, 'cnpj');
            this.scope.onRazaoSocialChanged = this.handleTextChanged.bind(this, 'razaoSocial');
            this.scope.onNomeFantasiaChanged = this.handleTextChanged.bind(this, 'nomeFantasia');
            this.scope.onRamoAtividadeChanged = this.handleTextChanged.bind(this, 'ramoAtividade');
            this.scope.onContactNameChanged = this.handleTextChanged.bind(this, 'contactName');
            this.scope.onContactEmailChanged = this.handleTextChanged.bind(this, 'contactEmail');
            this.scope.onContactPhoneChanged = this.handleTextChanged.bind(this, 'contactPhone');
            this.scope.onSubmit = this.action(this.onSubmit);
            this.scope.onClearFilters = this.action(this.onClearFilters);
            this.scope.onNew = this.action(this.onNew);
            this.scope.onPreviousPage = this.action(this.onMovePage.bind(this, -1));
            this.scope.onNextPage = this.action(this.onMovePage.bind(this, 1));
            this.scope.onGoToPage = this.action(this.onGoToPage);
            LOG.info('Initialized');
        }
        // O slot e decidido acima na composicao e pode mudar entre viagens: le-se sempre.
        this.parentSlot = keys.parentSlot;
        this.dialogSlot = keys.dialogSlot;
        this.at = {
            criteria: {
                cnpj: keys.cnpj,
                razaoSocial: keys.razaoSocial,
                nomeFantasia: keys.nomeFantasia,
                ramoAtividade: keys.ramoAtividade,
                contactName: keys.contactName,
                contactEmail: keys.contactEmail,
                contactPhone: keys.contactPhone,
                status: keys.status
            },
            sort: keys.sort,
            page: keys.page
        };
        if (last) {
            // Nada mais profundo esta aberto, entao o que a caixa de dialogo
            // guardava se foi.
            this.dialogSlot(undefined);
        }
        else {
            // Um dialogo esta aberto: a lista CONTINUA na tela atras dele, e e por
            // isso que o dialogo vai para o slot de dialogo do shell e nao para o
            // slot de conteudo.
            keys.dialogSlot = this.dialogSlot;
        }
        this.owner?.showSection('companies', 'Empresas', 'Para cadastrar uma nova empresa, solicitaremos alguns dados básicos');
        this.parentSlot(this.scope);
        this.scope.layout = this.table.show(this);
        await this.load();
        this.showRequest();
        this.showRows();
        return true;
    }
    publishParameters(intent) {
        const keys = new CompaniesKeys(this.app, intent);
        keys.cnpj = this.at.criteria.cnpj;
        keys.razaoSocial = this.at.criteria.razaoSocial;
        keys.nomeFantasia = this.at.criteria.nomeFantasia;
        keys.ramoAtividade = this.at.criteria.ramoAtividade;
        keys.contactName = this.at.criteria.contactName;
        keys.contactEmail = this.at.criteria.contactEmail;
        keys.contactPhone = this.at.criteria.contactPhone;
        keys.status = this.at.criteria.status;
        keys.sort = this.at.sort;
        keys.page = this.at.page;
    }
    /** As empresas em maos, para os dialogos nao buscarem de novo. */
    get companies() {
        return this.all;
    }
    companyOf(id) {
        return this.all.find((company) => company.id === id);
    }
    /**
     * Recarrega a lista do servidor.
     *
     * Publico porque e o que um dialogo chama depois de salvar: quem mudou o dado
     * avisa quem o mostra, e a lista volta a ser verdade sem a tela inteira
     * remontar.
     */
    async reload() {
        this.lastQuery = undefined;
        await this.load();
        this.showRows();
    }
    /**
     * Busca a PAGINA em vigor - e so quando ela mudou.
     *
     * Havia aqui um `if (this.all.length > 0) return`: a lista vinha uma vez e
     * ficava. Fazia sentido quando `this.all` era o acervo inteiro e a tela filtrava
     * sozinha; agora ele e uma pagina, e cada filtro, ordem ou pagina e uma pergunta
     * nova.
     *
     * O que substitui o guarda e a ASSINATURA da consulta. Sem ela, voltar de um
     * dialogo - que reaplica os parametros do place - buscaria de novo o que ja esta
     * na tela. Com ela, so o que muda a pergunta gasta rede.
     */
    async load() {
        const query = JSON.stringify({ criteria: this.at.criteria, sort: this.at.sort, page: this.at.page });
        if (query === this.lastQuery) {
            return;
        }
        this.lastQuery = query;
        const token = ++this.pending;
        this.scope.loading = true;
        this.scope.error = undefined;
        try {
            const criteria = this.at.criteria;
            const page = await companyApi.searchCompanyPage({
                cnpj: criteria.cnpj,
                razaoSocial: criteria.razaoSocial,
                nomeFantasia: criteria.nomeFantasia,
                ramoAtividade: criteria.ramoAtividade,
                contactName: criteria.contactName,
                contactEmail: criteria.contactEmail,
                contactPhone: criteria.contactPhone,
                status: criteria.status === 'active' ? 'ACTIVE' : criteria.status === 'blocked' ? 'BLOCKED' : undefined,
                sort: this.at.sort ?? DEFAULT_SORT
            }, this.at.page - 1, this.scope.perPage);
            if (token !== this.pending) {
                return;
            }
            this.all = page.elements ?? [];
            this.scope.total = page.count ?? this.all.length;
        }
        catch (caught) {
            if (token !== this.pending) {
                return;
            }
            this.scope.error = caught instanceof Error ? caught.message : 'Não foi possível buscar as empresas.';
        }
        finally {
            if (token === this.pending) {
                this.scope.loading = false;
            }
        }
    }
    /**
     * Mover o filtro, a ordem ou a pagina VOLTA ao servidor.
     *
     * Antes nao voltava: a tela tinha o acervo inteiro em memoria e refazia a conta
     * sozinha. Agora quem filtra, ordena e pagina e o banco, entao cada mudanca e um
     * pedido novo - e o `load` ja tem token de requisicao para duas respostas nao se
     * atropelarem.
     */
    async go(change) {
        this.at = { ...this.at, ...change };
        this.showRequest();
        this.updateHistory();
        await this.load();
        this.showRows();
    }
    showRequest() {
        const criteria = this.at.criteria;
        /*
         * So se escreve o que veio de FORA - ver `enviado`. Reescrever o que a propria
         * pessoa digitou devolveria o campo ao valor anterior no meio da digitacao.
         */
        for (const campo of CAMPOS_DE_TEXTO) {
            if (criteria[campo] !== this.enviado?.[campo]) {
                this.scope[campo] = criteria[campo] ?? '';
                this.scope.formRevision++;
            }
        }
        this.buildStatusFilter(criteria);
        this.buildColumns();
        this.scope.anyFilterActive = Object.keys(criteria).some((key) => criteria[key] !== UNFILTERED[key]);
    }
    /**
     * As linhas que o servidor mandou - ele ja filtrou, ordenou e paginou.
     *
     * Antes esta funcao fazia os tres sobre a lista inteira em memoria. O que sobrou
     * aqui e desenhar: `this.all` e a PAGINA, e nao mais o acervo.
     */
    showRows() {
        const pageCount = Math.max(1, Math.ceil(this.scope.total / this.scope.perPage));
        this.scope.page = Math.min(Math.max(1, this.at.page), pageCount);
        // As linhas que ja estao na tela sao atualizadas, e nao recriadas - ver `reuseRows`.
        this.scope.rows = reuseRows(this.scope.rows, this.all, (company) => company.id, (company, reuse) => this.buildRow(company, reuse));
    }
    buildRow(company, reuse) {
        const scope = reuse ?? new CompanyRowScope();
        scope.identity = company.id;
        scope.companyId = company.id;
        scope.cnpj = company.cnpj || '-';
        scope.razaoSocial = company.razaoSocial || '-';
        scope.nomeFantasia = company.nomeFantasia || '-';
        scope.ramoAtividade = company.ramoAtividade || '-';
        scope.contactName = company.contactName || '-';
        scope.contactEmail = company.contactEmail || '-';
        scope.contactPhone = company.contactPhone || '-';
        scope.active = company.active;
        scope.totalUsers = company.totalUsers;
        scope.onEdit = this.action(this.onEdit.bind(this, company.id));
        scope.onViewCollaborators = this.action(this.onViewCollaborators.bind(this, company.id));
        scope.onToggleAccess = this.action(this.onToggleAccess.bind(this, company));
        scope.onDelete = this.action(this.onConfirm.bind(this, company.id, 'delete'));
        scope.update = this.update;
        return scope;
    }
    buildColumns() {
        const current = this.at.sort ?? DEFAULT_SORT;
        const descending = current.startsWith('-');
        const field = descending ? current.slice(1) : current;
        // Na ordem que a pessoa arrastou; o CNPJ identifica a linha e nao sai do lugar. A
        // ordem e preferencia de quem le, e nao vai para o endereco.
        const all = COLUMNS.map((column) => column.field);
        const order = this.table.arrange(all, FIXED);
        // A coluna que ja esta na tela e o mesmo scope, atualizado - ver `reuseRows`.
        this.scope.columns = reuseRows(this.scope.columns, order, (name) => name, (name, reused) => {
            const column = COLUMNS.find((item) => item.field === name);
            const scope = reused ?? new CompanyColumnScope();
            scope.identity = column.field;
            scope.field = column.field;
            scope.label = column.label;
            scope.sorted = column.field === field;
            scope.descending = scope.sorted && descending;
            scope.movable = this.table.isMovable(all, column.field, FIXED);
            scope.onSort = this.action(this.onSort.bind(this, column.field));
            scope.onDrop = (source) => {
                if (this.table.move(all, source, column.field, FIXED)) {
                    this.buildColumns();
                }
            };
            scope.update = this.update;
            return scope;
        });
        this.table.setVisible([...order, ACTION_COLUMN]);
    }
    buildStatusFilter(criteria) {
        const filter = new StatusFilterScope();
        filter.identity = 'status';
        filter.active = criteria.status !== undefined;
        filter.update = this.update;
        // "STATUS" e "Todos" sao os rotulos da referencia; o porte dizia "Situação"
        // e "Todas".
        filter.label = 'STATUS';
        const options = [
            { label: 'Todos', value: undefined },
            { label: 'Ativas', value: 'active' },
            { label: 'Bloqueadas', value: 'blocked' }
        ];
        filter.summary = options.find((option) => option.value === criteria.status)?.label ?? 'Todos';
        filter.options = options.map((option) => {
            const scope = new StatusOptionScope();
            scope.identity = `status:${option.value ?? ''}`;
            scope.label = option.label;
            scope.value = option.value;
            scope.current = option.value === criteria.status;
            scope.onSelect = this.action(async () => {
                await this.go({ criteria: { ...criteria, status: option.value }, page: 1 });
            });
            scope.update = this.update;
            return scope;
        });
        this.scope.statusFilter = filter;
    }
    // ========== ACOES ==========
    handleTextChanged(field, value) {
        // O CNPJ ganha mascara enquanto se digita, como no filtro da referencia
        // (`handleMasks(text, 'cnpj')`). O porte mostrava os catorze digitos crus.
        //
        // O telefone tambem, e por um motivo pratico: o valor esta GRAVADO com
        // mascara ("(54) 99103-7668"), entao procurar por digitos crus nao acha
        // nada. Este filtro e adicao do porte - na referencia ele nao existe.
        const shown = field === 'cnpj' ? formatCnpj(value) : field === 'contactPhone' ? maskPhone(value) : value;
        this.scope[field] = shown;
        /*
         * A busca sai depois de a pessoa PARAR de digitar.
         *
         * O botao "Aplicar busca" saiu em 18/09/2026, junto com o dos Monitoramentos: o
         * filtro passou a valer sozinho. Sem a espera seriam sete consultas para escrever
         * "Mormaii", e sete entradas no historico - o Voltar desfaria letra por letra.
         */
        clearTimeout(this.espera);
        this.espera = setTimeout(() => void this.aplicar(), BUSCA_DEBOUNCE_MS);
    }
    /** Manda o que esta na tela para o endereco, e com ele para o servidor. */
    async aplicar() {
        clearTimeout(this.espera);
        const criteria = {
            // `status` nao esta nos campos de texto: ele e escolha discreta e ja vive no
            // criterio, entao vem de la para nao ser perdido a cada tecla.
            ...this.at.criteria,
            cnpj: this.scope.cnpj || undefined,
            razaoSocial: this.scope.razaoSocial || undefined,
            nomeFantasia: this.scope.nomeFantasia || undefined,
            ramoAtividade: this.scope.ramoAtividade || undefined,
            contactName: this.scope.contactName || undefined,
            contactEmail: this.scope.contactEmail || undefined,
            contactPhone: this.scope.contactPhone || undefined
        };
        this.enviado = criteria;
        await this.go({ criteria, page: 1 });
    }
    /**
     * O Enter, que adianta a espera.
     *
     * O botao de aplicar saiu, mas o `<form>` continua submetendo no Enter, e quem aperta
     * nao quer esperar os 400ms.
     */
    async onSubmit() {
        await this.aplicar();
    }
    /**
     * Limpar zera os campos AQUI, e nao espera o `showRequest`.
     *
     * Ele nao os tocaria: o criterio limpo e exatamente o que acabou de ser enviado, e a
     * guarda contra a corrida existe para nao reescrever o que a pessoa digitou.
     */
    async onClearFilters() {
        clearTimeout(this.espera);
        this.scope.cnpj = '';
        this.scope.razaoSocial = '';
        this.scope.nomeFantasia = '';
        this.scope.ramoAtividade = '';
        this.scope.contactName = '';
        this.scope.contactEmail = '';
        this.scope.contactPhone = '';
        this.scope.formRevision++;
        this.enviado = { ...UNFILTERED };
        await this.go({ criteria: { ...UNFILTERED }, page: 1 });
    }
    async onSort(field) {
        const current = this.at.sort;
        const sort = current === field ? `-${field}` : current === `-${field}` ? undefined : field;
        await this.go({ sort, page: 1 });
    }
    /** Ir direto a uma pagina - o paginador numerado da referencia tem isso. */
    async onGoToPage(page) {
        const alvo = Math.min(Math.max(1, page), this.scope.pageCount);
        if (alvo !== this.at.page) {
            await this.go({ page: alvo });
        }
    }
    async onMovePage(direction) {
        const page = Math.min(Math.max(1, this.at.page + direction), this.scope.pageCount);
        if (page !== this.at.page) {
            await this.go({ page });
        }
    }
    /** Cadastrar: o mesmo dialogo do editar, sem empresa alvo. */
    async onNew() {
        await this.openEdit(undefined);
    }
    async onEdit(companyId) {
        await this.openEdit(companyId);
    }
    /**
     * Abre o formulario, que e um place DENTRO desta lista.
     *
     * Nao ha filtro a copiar: `of` cria o intent do dialogo e o framework o
     * preenche perguntando aos presenters vivos - esta lista e um deles, e o que
     * ela publica e exatamente o filtro que esta na tela. Fechar o dialogo faz o
     * caminho inverso pelo mesmo mecanismo.
     */
    async openEdit(companyId) {
        const target = CompanyEditKeys.of(this.app);
        target.target = companyId;
        await target.flip();
    }
    /**
     * Ver os colaboradores desta empresa.
     *
     * No painel antigo isso navega com `?companyId=` e a tela de colaboradores le o
     * parametro na montagem, uma vez. Aqui e o mesmo parametro de place que a lista
     * ja usa - a travessia entre telas passou a ser a coisa normal em vez de um
     * caso especial.
     */
    async onViewCollaborators(companyId) {
        const target = CollaboratorsKeys.of(this.app);
        target.companyId = companyId;
        await target.flip();
    }
    /**
     * Bloquear ou reativar.
     *
     * Bloquear pede confirmacao, reativar nao - e o que o painel antigo faz, e faz
     * sentido: uma das duas tira acesso de gente.
     */
    /**
     * Bloquear ou liberar - os dois pedem confirmacao (decisao de 08/10/2026).
     *
     * Liberar era direto, sem perguntar. Passou pelo mesmo dialogo, que mostra o que a
     * acao faz e, ao liberar, as tentativas de acesso feitas durante o bloqueio.
     */
    async onToggleAccess(company) {
        await this.onConfirm(company.id, company.active ? 'block' : 'unblock');
    }
    async onConfirm(companyId, action) {
        const target = CompanyConfirmKeys.of(this.app);
        target.target = companyId;
        target.action = action;
        await target.flip();
    }
}
/** O CNPJ - a primeira coluna - fica; a de acoes a view poe depois de todas. */
const FIXED = { start: 1 };
function companyTableModel(account) {
    const model = new TableModel({
        name: 'companies',
        account,
        legacy: { widths: 'mormaii.columnWidths:companies' }
    });
    model.setColumns([
        ...COLUMNS.map((column) => ({ field: column.field })),
        { field: ACTION_COLUMN, resizable: false }
    ]);
    model.setVisible([...COLUMNS.map((column) => column.field), ACTION_COLUMN]);
    return model;
}
//# sourceMappingURL=companies.presenter.js.map